/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `gyroscope_data` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class GyroscopeDataMesg : Mesg {
    public constructor() : super("GyroscopeData", Profile.MesgNum.GYROSCOPE_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Whole second part of the timestamp */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond part of the timestamp. */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** Number of values stored in sample_time_offset. */
    public val numSampleTimeOffset: Int
        get() = getField(SAMPLE_TIME_OFFSET_FIELD_NUM)?.numValues ?: 0

    /** sample_time_offset, in ms. Each time in the array describes the time at which the gyro sample with the corrosponding index was taken. Limited to 30 samples in each message. The samples may span across seconds. Array size must match the number of samples in gyro_x and gyro_y and gyro_z */
    public var sampleTimeOffset: List<UShort?>?
        get() {
            val field = getField(SAMPLE_TIME_OFFSET_FIELD_NUM) ?: return null
            return List(field.numValues) { getSampleTimeOffset(it) }
        }
        set(value) {
            removeField(SAMPLE_TIME_OFFSET_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSampleTimeOffset(index, element) }
        }

    public fun getSampleTimeOffset(index: Int): UShort? =
        getFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, index) as? UShort

    public fun setSampleTimeOffset(index: Int, value: UShort?) {
        setFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, value, index)
    }

    /** Number of values stored in gyro_x. */
    public val numGyroX: Int
        get() = getField(GYRO_X_FIELD_NUM)?.numValues ?: 0

    /** gyro_x, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var gyroX: List<UShort?>?
        get() {
            val field = getField(GYRO_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getGyroX(it) }
        }
        set(value) {
            removeField(GYRO_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGyroX(index, element) }
        }

    public fun getGyroX(index: Int): UShort? =
        getFieldValue(GYRO_X_FIELD_NUM, index) as? UShort

    public fun setGyroX(index: Int, value: UShort?) {
        setFieldValue(GYRO_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in gyro_y. */
    public val numGyroY: Int
        get() = getField(GYRO_Y_FIELD_NUM)?.numValues ?: 0

    /** gyro_y, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var gyroY: List<UShort?>?
        get() {
            val field = getField(GYRO_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getGyroY(it) }
        }
        set(value) {
            removeField(GYRO_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGyroY(index, element) }
        }

    public fun getGyroY(index: Int): UShort? =
        getFieldValue(GYRO_Y_FIELD_NUM, index) as? UShort

    public fun setGyroY(index: Int, value: UShort?) {
        setFieldValue(GYRO_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in gyro_z. */
    public val numGyroZ: Int
        get() = getField(GYRO_Z_FIELD_NUM)?.numValues ?: 0

    /** gyro_z, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var gyroZ: List<UShort?>?
        get() {
            val field = getField(GYRO_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getGyroZ(it) }
        }
        set(value) {
            removeField(GYRO_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGyroZ(index, element) }
        }

    public fun getGyroZ(index: Int): UShort? =
        getFieldValue(GYRO_Z_FIELD_NUM, index) as? UShort

    public fun setGyroZ(index: Int, value: UShort?) {
        setFieldValue(GYRO_Z_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_gyro_x. */
    public val numCalibratedGyroX: Int
        get() = getField(CALIBRATED_GYRO_X_FIELD_NUM)?.numValues ?: 0

    /** calibrated_gyro_x, in deg/s. Calibrated gyro reading */
    public var calibratedGyroX: List<Float?>?
        get() {
            val field = getField(CALIBRATED_GYRO_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedGyroX(it) }
        }
        set(value) {
            removeField(CALIBRATED_GYRO_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedGyroX(index, element) }
        }

    public fun getCalibratedGyroX(index: Int): Float? =
        getFieldValue(CALIBRATED_GYRO_X_FIELD_NUM, index) as? Float

    public fun setCalibratedGyroX(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_GYRO_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_gyro_y. */
    public val numCalibratedGyroY: Int
        get() = getField(CALIBRATED_GYRO_Y_FIELD_NUM)?.numValues ?: 0

    /** calibrated_gyro_y, in deg/s. Calibrated gyro reading */
    public var calibratedGyroY: List<Float?>?
        get() {
            val field = getField(CALIBRATED_GYRO_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedGyroY(it) }
        }
        set(value) {
            removeField(CALIBRATED_GYRO_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedGyroY(index, element) }
        }

    public fun getCalibratedGyroY(index: Int): Float? =
        getFieldValue(CALIBRATED_GYRO_Y_FIELD_NUM, index) as? Float

    public fun setCalibratedGyroY(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_GYRO_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_gyro_z. */
    public val numCalibratedGyroZ: Int
        get() = getField(CALIBRATED_GYRO_Z_FIELD_NUM)?.numValues ?: 0

    /** calibrated_gyro_z, in deg/s. Calibrated gyro reading */
    public var calibratedGyroZ: List<Float?>?
        get() {
            val field = getField(CALIBRATED_GYRO_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedGyroZ(it) }
        }
        set(value) {
            removeField(CALIBRATED_GYRO_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedGyroZ(index, element) }
        }

    public fun getCalibratedGyroZ(index: Int): Float? =
        getFieldValue(CALIBRATED_GYRO_Z_FIELD_NUM, index) as? Float

    public fun setCalibratedGyroZ(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_GYRO_Z_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val SAMPLE_TIME_OFFSET_FIELD_NUM: UByte = 1u
        public const val GYRO_X_FIELD_NUM: UByte = 2u
        public const val GYRO_Y_FIELD_NUM: UByte = 3u
        public const val GYRO_Z_FIELD_NUM: UByte = 4u
        public const val CALIBRATED_GYRO_X_FIELD_NUM: UByte = 5u
        public const val CALIBRATED_GYRO_Y_FIELD_NUM: UByte = 6u
        public const val CALIBRATED_GYRO_Z_FIELD_NUM: UByte = 7u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("SampleTimeOffset", 1u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[2] = Field("GyroX", 2u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[3] = Field("GyroY", 3u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[4] = Field("GyroZ", 4u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[5] = Field("CalibratedGyroX", 5u, BaseType.FLOAT32, 1.0, 0.0, "deg/s", false)
            fields[6] = Field("CalibratedGyroY", 6u, BaseType.FLOAT32, 1.0, 0.0, "deg/s", false)
            fields[7] = Field("CalibratedGyroZ", 7u, BaseType.FLOAT32, 1.0, 0.0, "deg/s", false)
            return fields
        }
    }
}
